import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Heart, Star } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useI18n } from "../i18n";
import { formatPrice, discountedPrice } from "../lib/api";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { addToCart, toggleWishlist, inWishlist } = useCart();
  const { t, lang } = useI18n();
  const inWl = inWishlist(product.id);
  const finalPrice = discountedPrice(product);

  const onAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const r = await addToCart(product.id, 1);
    if (r.requireLogin) {
      toast.error(t("common.please_login"));
    } else if (r.ok) {
      toast.success(t("common.added"));
    }
  };

  const onWl = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const r = await toggleWishlist(product.id);
    if (r.requireLogin) toast.error(t("common.please_login"));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.04, 0.4) }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.slug || product.id}`}>
        <div className="relative rounded-2xl overflow-hidden bg-white border border-slate-200 transition-all duration-300 hover:shadow-[0_18px_50px_rgba(168,85,247,0.22)] hover:-translate-y-1.5 hover:border-[#A855F7]/40">
          {/* Cover */}
          <div className="aspect-[3/4] relative overflow-hidden bg-slate-100">
            <img
              src={product.cover_image}
              alt={product.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

            {/* Discount badge */}
            {product.discount_percent > 0 && (
              <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#A855F7] text-white text-xs font-bold shadow-neon-soft" data-testid="discount-badge">
                -{Math.round(product.discount_percent)}%
              </div>
            )}

            {/* Wishlist */}
            <button
              onClick={onWl}
              className={`absolute top-3 right-3 w-9 h-9 rounded-full grid place-items-center backdrop-blur transition-all ${
                inWl ? "bg-[#A855F7] text-white" : "bg-white/80 text-slate-700 hover:bg-white"
              }`}
              data-testid={`wishlist-btn-${product.id}`}
            >
              <Heart className={`w-4 h-4 ${inWl ? "fill-current" : ""}`} />
            </button>

            {/* Bottom add to cart */}
            <button
              onClick={onAdd}
              className="absolute bottom-3 right-3 w-10 h-10 rounded-xl bg-[#A855F7] text-white grid place-items-center shadow-neon-soft hover:bg-[#9333EA] transition-all opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0"
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingCart className="w-4 h-4" />
            </button>
          </div>

          {/* Info */}
          <div className="p-4">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-display font-semibold text-sm text-slate-900 line-clamp-1">{product.title}</h3>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
              <span>{product.platform}</span>
              {product.rating && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-0.5">
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    {product.rating}
                  </span>
                </>
              )}
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-lg font-bold text-slate-900" data-testid={`product-price-${product.id}`}>
                {formatPrice(finalPrice, lang)}
              </span>
              {product.discount_percent > 0 && (
                <span className="text-xs text-slate-400 line-through">{formatPrice(product.price, lang)}</span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
